import React from 'react';
import oh from 'output-helpers';

import styled, {keyframes} from '../../theme/styled';
import Minimize from './Minimize';

import IconTemperature from '../../images/icons/icon_temperature.svg';
import IconHumidity from '../../images/icons/icon_humidity.svg';
import IconCo2 from '../../images/icons/icon_co2.svg';
import IconBoolean from '../../images/icons/icon_boolean.svg';
import IconDistance from '../../images/icons/icon_distance.svg';
import IconPressure from '../../images/icons/icon_pressure.svg';
import IconParking from '../../images/icons/icon_parking.svg';
import IconLighting from '../../images/icons/icon_lighting.svg';
import IconSound from '../../images/icons/icon_sound.svg';
import IconWaterLeak from '../../images/icons/icon_water-leak.svg';
import IconMotionSensor from '../../images/icons/icon_motion-sensor.svg';
import IconBatteryLevel from '../../images/icons/icon_battery-level.svg';

interface RightPanelProps {
    /*onEditClick: () => void;
    onSaveClick: () => void;
    onCancelClick: () => void;
    editing: boolean;
    selected_device_id: string | null;
    create: boolean;
    onDeviceCreated: (device_id: string) => void;
    onDeleteClick: () => void;
    */
}

const anim = keyframes`
    from {
        transform: translateX(250px);
        opacity: 0.5;
    }
    to {
        transform: translateX(0px);
        opacity: 1;
    }
`;

const RightPanelContainer = styled.div`
    animation: ${anim} 0.8s;
    background-color: ${props => props.theme.colors.background};
    box-shadow: ${props => props.theme.components.right_panel.shadow};
    bottom: 0;
    right: 0;
    top: 0;
    position: fixed;
    width: ${props => props.theme.components.right_panel.width}px;
    z-index: 99;
    @media(max-width: 764px) {
        width: 100%;
    }
    p.select_label, p.multi_select_label {
        margin-bottom: 6px;
    }
    .right_panel_body {
        position: absolute;
        top: ${props => props.theme.sizes.panel.height}px;
        left: 0;
        right: 0;
        bottom: 0;
        overflow: auto;
        .right_panel_body_padding {
            background-color: #fff;
            padding: 4px 16px 30px;
        }
    }
`;

const WidgetsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${props => props.theme.sizes.padding.horizontal}px;
    grid-row-gap: ${props => props.theme.sizes.padding.horizontal}px;
    padding: ${props => props.theme.sizes.padding.horizontal}px;
`;

interface WidgetContainerProps {
    main?: boolean;
}

const WidgetContainer = styled.div<WidgetContainerProps>`
    background-color: ${props => props.main ? props.theme.colors.brand[1] : '#fff'};
    color: ${props => props.main ? '#fff' : props.theme.colors.text};
    border: 1px solid ${props => props.main ? props.theme.colors.brand[1] : props.theme.colors.border};
    border-radius: 6px;
    height: 100px;
    padding: 12px;
    display: flex;
    position: relative;
    .icon {
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        width: 80px;
        height: 80px;
        position: absolute;
        right: 0;
        top: 10px;
        &.temperature {
            background-image: url(${IconTemperature});
        }
        &.humidity {
            background-image: url(${IconHumidity});
        }
        &.co2 {
            background-image: url(${IconCo2});
        }
        &.boolean {
            background-image: url(${IconBoolean});
        }
        &.distance {
            background-image: url(${IconDistance});
        }
        &.pressure {
            background-image: url(${IconPressure});
        }
        &.parking {
            background-image: url(${IconParking});
        }
        &.light {
            background-image: url(${IconLighting});
        }
        &.water-leak {
            background-image: url(${IconWaterLeak});
        }
        &.sound {
            background-image: url(${IconSound});
        }
        &.motion-sensor {
            background-image: url(${IconMotionSensor});
        }
        &.battery-level {
            background-image: url(${IconBatteryLevel});
        }
    }
    .value {
        font-size: 44px;
        font-weight: 700;
        align-self: flex-end;
        left: 12px;
        position: absolute;
    }
    .label{
        font-weight: 400;
        font-size: 16px;
        opacity: 0.7;
    }
`;

const TopBarContainer = styled.div`
    align-items: center;
    background-color: #fff;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    display: grid;
    grid-template-columns: 80% 20%;
    height: ${props => props.theme.sizes.panel.height}px;
    padding: 0 ${props => props.theme.sizes.padding.horizontal}px;
    transition: background-color 0.3s;
    .device_labels{
        .device_name {
            font-size: ${props => props.theme.sizes.fonts.medium}px;
            font-weight: bold;
            margin-bottom: 2px;
            margin-right: 8px;
            vertical-align: middle;
            ${props => props.theme.mixins.truncate};
        }
        .device_id {
            color: ${props => props.theme.colors.subtitle};
            font-size: ${props => props.theme.sizes.fonts.small}px;
            ${props => props.theme.mixins.truncate};
            vertical-align: middle;
        }
    }
    &.editing {
        background-color: ${props => props.theme.colors.red[2]};
        border-color: ${props => props.theme.colors.red[2]};
        grid-template-columns: 2fr 1fr;
        button:nth-child(2) {// TODO: REMOVE
            justify-self: end;
        }
        button {
            width: 100px;
        }
        .device_labels {
            .device_name, .device_id {
                color: #fff;
            }
        }
    }
    @media(max-width: 764px) {
        .device_labels {
            padding-left: 40px;
        }
    }
`;

const DetailsContainer = styled.div`
    background-color: white;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    .category {
        font-weight: 700;
        border-bottom: 1px solid ${props => props.theme.colors.border};
        border-top: 1px solid ${props => props.theme.colors.border};
        padding: 10px ${props => props.theme.sizes.padding.horizontal}px;
    }
    .category_items {
        padding: 10px ${props => props.theme.sizes.padding.horizontal}px;
        .category_item {}
    }
    .tags_container {
        padding: 10px ${props => props.theme.sizes.padding.horizontal}px 6px;
    }
    .tag {
        display: inline-block;
        border-radius: 3px;
        font-size: ${props => props.theme.sizes.fonts.small}px;
        padding: 4px 8px;
        margin: 0px 4px 4px 0px;
        background-color: ${props => props.theme.colors.brand[3]};
        color: white;
    }
    .no_tags {
        display: inline-block;
        border: 2px solid ${props => props.theme.colors.gray[2]};
        color: ${props => props.theme.colors.gray[1]};
        border-style: dashed;
        border-radius: 3px;
        padding: 4px 6px;
        margin: 0px 4px 4px 0px;
    }
`;

const TopBar = () => {
    return (
        <TopBarContainer>
            <div className="device_labels">
                <div className="device_name">{'props.device.name'}</div>
                <div className="device_id">{'props.device.id'}</div>
            </div>
        </TopBarContainer>
    );
};

const Details = () => {
    return (
        <DetailsContainer>
            <div className="category">{oh.translate("tags")}</div>
            <div className="category_items tags_container">
            <div className="tag" key={"tag_"}>{'tag'}</div>
            </div>
            <div className="category">{oh.translate("device_model")}</div>
            <div className="category_items">manufacturer.name</div>
        </DetailsContainer>
    );
};


const Widgets = () => {
    return (
        <WidgetsContainer>
            <WidgetContainer
                className={`${'sound'}`}
                main={true}>
                <div className="value">{4}</div>
                <div className="label">{'Sound'}</div>
                <div className={`icon ${'sound'}`}/>
            </WidgetContainer>
        </WidgetsContainer>
    );
};


const RightPanel = (props: RightPanelProps) => {

    return (
        <RightPanelContainer>
            <Minimize
                direction="left"
                onClick={() => console.log('onClick')}/>
            <TopBar/>
            <div className="right_panel_body">
                <Widgets/>
                <Details/>
            </div>
        </RightPanelContainer>
    );
};

export default RightPanel;



// WEBPACK FOOTER //
// ./src/components/base/RightPanel.tsx